import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ItemComponent from "../components";
import {
  getItems,
  deleteItem,
} from "../../../../services/Handlers/MasterItemsService";
import { getCategoryItems } from "../../../../services/Handlers/MasterCategoryItemService";
import { useAuth } from "../../../../context/AuthProvider";
import { useNotification } from "../../../../context/NotificationProvider";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import { useModal } from "../../../../context/ModalProvider";
import { PER_PAGE_SIZE } from "../../../../utils/constants";

export default function Item() {
  const localize = useLocalization();
  const notification = useNotification();
  const modal = useModal();
  const queryClient = useQueryClient();

  const { userDetail } = useAuth();
  const [dataItem, setDataItem] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(PER_PAGE_SIZE);
  const [sortState, setSortState] = useState({ key: "", value: "" });

  // Search: raw input (immediate) + debounced value (used for query).
  // Debounce 300ms; treat <2 chars as no filter.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryItemId, setCategoryItemId] = useState("");
  const [dataCategoryItem, setDataCategoryItem] = useState([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const next = searchInput.trim().length >= 2 ? searchInput.trim() : "";
      setSearchQuery((prev) => {
        if (prev !== next) setPage(0);
        return next;
      });
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // TODO: swap to async searchable combobox when tenants exceed 500 categories
  useQuery(
    ["CategoryItemList", userDetail?.businessId],
    () => getCategoryItems({ businessId: userDetail?.businessId, size: 500 }),
    {
      onSuccess: (res) => {
        if (res?.status === 200 || res?.status === 201) {
          setDataCategoryItem(res?.data?.data || []);
        }
      },
      enabled: !!userDetail?.businessId,
    },
  );

  const { isLoading, isFetching, refetch } = useQuery(
    [
      "ItemList",
      userDetail?.businessId,
      page,
      sizePerPage,
      sortState,
      searchQuery,
      categoryItemId,
    ],
    () =>
      getItems({
        businessId: userDetail?.businessId,
        page,
        size: sizePerPage,
        sortKey: sortState?.key,
        sortValue: sortState?.value,
        search: searchQuery,
        categoryItemId,
      }),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setDataItem(res?.data.data || []);
          setTotalEntries(res?.data?.pagination?.total || 0);
          setTotalPage(
            Math.ceil((res?.data?.pagination?.total || 0) / sizePerPage),
          );
        }
      },
      enabled: !!userDetail?.businessId,
    },
  );

  const { isLoading: isLoadingDelete, mutate: mutateDelete } = useMutation(
    deleteItem,
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          modal.dismiss();
          queryClient.invalidateQueries({
            queryKey: ["ItemList", userDetail?.businessId],
          });
          refetch();
          notification.success(localize.getText("successDeleteData"));
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError"),
          );
        }
      },
      onError: () => {
        notification.error(localize.getText("unknownMessageError"));
      },
    },
  );

  const handlePagination = (value) => {
    setPage(value);
  };

  const handleSetSizePerPage = (size) => {
    setPage(0);
    setSizePerPage(size);
  };

  const handleSetSortState = (next) => {
    setPage(0);
    setSortState(next);
  };

  const handleSetCategoryItemId = (next) => {
    setPage(0);
    setCategoryItemId(next);
  };

  return (
    <ItemComponent
      dataItem={dataItem}
      isLoading={isLoading || isFetching || isLoadingDelete}
      mutateDelete={mutateDelete}
      totalPage={totalPage}
      totalEntries={totalEntries}
      activePage={page}
      handlePagination={handlePagination}
      handleSetSizePerPage={handleSetSizePerPage}
      sizePerPage={sizePerPage}
      sortState={sortState}
      setSortState={handleSetSortState}
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      categoryItemId={categoryItemId}
      setCategoryItemId={handleSetCategoryItemId}
      dataCategoryItem={dataCategoryItem}
    />
  );
}
